/**
 * FileViewer 的格式登记：扩展名 → 引擎种类、MIME、文本解码、字节嗅探。
 * 纯函数，不依赖 UI / store。
 */

export type FileViewerKind = 'pdf' | 'docx' | 'pptx' | 'sheet' | 'text' | 'image';

const KIND_BY_EXT: Record<string, FileViewerKind> = {
  pdf: 'pdf',
  docx: 'docx',
  pptx: 'pptx',
  xlsx: 'sheet',
  xlsm: 'sheet',
  csv: 'sheet',
  tsv: 'sheet',
  md: 'text',
  markdown: 'text',
  txt: 'text',
  log: 'text',
  json: 'text',
  xml: 'text',
  yaml: 'text',
  yml: 'text',
  png: 'image',
  jpg: 'image',
  jpeg: 'image',
  gif: 'image',
  webp: 'image',
  bmp: 'image',
  svg: 'image',
};

/** 旧二进制 Office 格式：浏览器解析不了，交给后端 to_pdf 转成 PDF 再看 */
export const LEGACY_OFFICE_EXT = new Set(['doc', 'xls', 'ppt']);

const MIME: Record<string, string> = {
  pdf: 'application/pdf',
  docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  xlsm: 'application/vnd.ms-excel.sheet.macroEnabled.12',
  pptx: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  csv: 'text/csv;charset=utf-8',
  tsv: 'text/tab-separated-values;charset=utf-8',
  md: 'text/markdown;charset=utf-8',
  png: 'image/png',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  gif: 'image/gif',
  webp: 'image/webp',
  bmp: 'image/bmp',
  svg: 'image/svg+xml',
};

export const extOf = (name: string): string => {
  const trimmed = name.trim();
  const dot = trimmed.lastIndexOf('.');
  return dot > 0 ? trimmed.slice(dot + 1).toLowerCase() : '';
};

export const kindOfExt = (ext: string): FileViewerKind | undefined => KIND_BY_EXT[ext];

export const mimeOf = (ext: string): string =>
  MIME[ext] || (KIND_BY_EXT[ext] === 'text' ? 'text/plain;charset=utf-8' : 'application/octet-stream');

export const escHtml = (s: string): string =>
  s.replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c] as string));

export interface DecodedText {
  text: string;
  /** 未做换行归一化的原文（CSV 需要区分记录分隔符与字段内换行） */
  raw: string;
  encoding: 'utf-8' | 'shift_jis';
  bom: boolean;
  crlf: boolean;
}

/** 文本解码：UTF-8（含 BOM）优先，失败按 Shift_JIS（日本院内 CSV / TXT 常见） */
export const decodeText = (buf: ArrayBuffer): DecodedText => {
  const u8 = new Uint8Array(buf);
  const bom = u8[0] === 0xef && u8[1] === 0xbb && u8[2] === 0xbf;
  let text: string;
  let encoding: DecodedText['encoding'] = 'utf-8';
  try {
    text = new TextDecoder('utf-8', { fatal: true }).decode(bom ? u8.subarray(3) : u8);
  } catch {
    text = new TextDecoder('shift_jis').decode(u8);
    encoding = 'shift_jis';
  }
  return { text: text.replace(/\r\n/g, '\n'), raw: text, encoding, bom, crlf: /\r\n/.test(text) };
};

/** 从下载响应的 Content-Disposition 里取文件名（CORS 没暴露该头时返回空串） */
export const fileNameFromDisposition = (header?: string | null): string => {
  if (!header) return '';
  const utf8 = header.match(/filename\*=(?:UTF-8|utf-8)''([^;]+)/);
  if (utf8) {
    try {
      return decodeURIComponent(utf8[1].trim().replace(/^"|"$/g, ''));
    } catch {
      /* 继续用普通 filename */
    }
  }
  const plain = header.match(/filename="?([^";]+)"?/);
  if (!plain) return '';
  const name = plain[1].trim();
  // 后端把普通 filename 也按 URL 编码输出（%E8%A1...%20.pdf），认得出就解回来
  if (/%[0-9A-Fa-f]{2}/.test(name)) {
    try {
      return decodeURIComponent(name);
    } catch {
      /* 不是合法编码，原样返回 */
    }
  }
  return name;
};

const startsWith = (u8: Uint8Array, bytes: number[]) => bytes.every((b, i) => u8[i] === b);

/**
 * 没有可靠文件名时按字节嗅探格式。
 * zip 容器（docx / pptx / xlsx 都是 zip）要看里面的目录名，交给调用方用 JSZip 判；这里只认魔数。
 * 返回 'zip' 表示需要进一步看目录，'ole' 表示旧版 Office 二进制。
 */
export const sniffMagic = (buf: ArrayBuffer): FileViewerKind | 'zip' | 'ole' | undefined => {
  const u8 = new Uint8Array(buf.slice(0, 12));
  if (startsWith(u8, [0x25, 0x50, 0x44, 0x46])) return 'pdf'; // %PDF
  if (startsWith(u8, [0x50, 0x4b, 0x03, 0x04])) return 'zip'; // PK
  if (startsWith(u8, [0xd0, 0xcf, 0x11, 0xe0])) return 'ole'; // doc / xls / ppt
  if (startsWith(u8, [0x89, 0x50, 0x4e, 0x47])) return 'image'; // png
  if (startsWith(u8, [0xff, 0xd8, 0xff])) return 'image'; // jpeg
  if (startsWith(u8, [0x47, 0x49, 0x46, 0x38])) return 'image'; // gif
  if (startsWith(u8, [0x42, 0x4d])) return 'image'; // bmp
  if (startsWith(u8, [0x52, 0x49, 0x46, 0x46]) && u8[8] === 0x57 && u8[9] === 0x45) return 'image'; // webp
  return undefined;
};

/** zip 容器按目录名判 Office 格式 */
export const kindOfZipEntries = (paths: string[]): { kind: FileViewerKind; ext: string } | undefined => {
  if (paths.some((p) => p.startsWith('word/'))) return { kind: 'docx', ext: 'docx' };
  if (paths.some((p) => p.startsWith('ppt/'))) return { kind: 'pptx', ext: 'pptx' };
  if (paths.some((p) => p.startsWith('xl/'))) return { kind: 'sheet', ext: 'xlsx' };
  return undefined;
};

/** 字节是否像可读文本（UTF-8 或 Shift_JIS 能解、且没有大量控制字符） */
export const looksLikeText = (buf: ArrayBuffer): boolean => {
  const u8 = new Uint8Array(buf.slice(0, 4096));
  if (!u8.length) return true;
  let control = 0;
  for (const b of u8) {
    if (b === 0) return false;
    if (b < 0x20 && b !== 0x09 && b !== 0x0a && b !== 0x0d) control++;
  }
  return control / u8.length < 0.02;
};
